import { useQuery } from '@tanstack/react-query';
import {
  verificationPhotoApi,
  type VerificationPhoto,
  type PatientVerificationPhoto,
} from '../services/verificationPhotoService';

/**
 * PRX-51 / PRX-68 — the same key the verification dialog and the order
 * timeline use for one order's photos, so a single order page shares one
 * fetch instead of issuing it three times.
 */
export const verificationPhotosQueryKey = (pharmacyId?: string, orderId?: string) =>
  ['verification-photos', pharmacyId, orderId] as const;

export function useVerificationPhotos(
  pharmacyId?: string,
  orderId?: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery<VerificationPhoto[]>({
    queryKey: verificationPhotosQueryKey(pharmacyId, orderId),
    queryFn: () => verificationPhotoApi.list(pharmacyId as string, orderId as string),
    enabled: !!pharmacyId && !!orderId && (options.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * PRX-68 — every live photo across all of a patient's orders at one
 * pharmacy. The Rx-centric Patient 360 prescription dialog has no order id
 * to key off, so it asks by patient instead.
 */
export const patientVerificationPhotosQueryKey = (pharmacyId?: string, patientId?: string) =>
  ['verification-photos-by-patient', pharmacyId, patientId] as const;

export function usePatientVerificationPhotos(pharmacyId?: string, patientId?: string) {
  return useQuery<PatientVerificationPhoto[]>({
    queryKey: patientVerificationPhotosQueryKey(pharmacyId, patientId),
    queryFn: () => verificationPhotoApi.listForPatient(pharmacyId as string, patientId as string),
    enabled: !!pharmacyId && !!patientId,
    staleTime: 60_000,
  });
}
